<template>
  <v-container fluid>
    <v-card class="mb-4">
      <v-card-title class="headline">Staff Activity</v-card-title>
      <v-card-text>
        <div class="subtitle-2 grey--text mb-4">
          Daily Smart Advocate counts, weighted by role and ranked
          {{ firmView ? "across the firm" : "inside each division" }}. The
          comparison is the same number of days just before this range.
        </div>
        <v-row>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="startDate"
              type="date"
              label="From"
              dense
              outlined
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="endDate"
              type="date"
              label="To"
              dense
              outlined
              hide-details
            ></v-text-field>
          </v-col>
          <v-col v-if="!firmView" cols="12" sm="3">
            <v-select
              v-model="divisionId"
              :items="divisionOptions"
              label="Division"
              dense
              outlined
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" sm="3" class="d-flex align-center">
            <v-switch
              v-model="firmView"
              label="Whole firm"
              hide-details
              class="mt-0"
            ></v-switch>
          </v-col>
          <v-col cols="12" sm="3" class="d-flex align-center">
            <v-btn
              color="primary"
              :disabled="!missingDates.length || capturing"
              :loading="capturing"
              @click="captureMissing"
            >
              Capture missing days
            </v-btn>
          </v-col>
        </v-row>
        <div class="mt-3 caption grey--text">
          {{ daysCaptured }} activity day{{ daysCaptured === 1 ? "" : "s" }} and
          {{ computerDaysCaptured }} ActivTrak day{{
            computerDaysCaptured === 1 ? "" : "s"
          }}
          in this range. Hours are productive computer time. Score/hr divides
          the score by those hours. Rank still uses the score.
          <span v-if="missingDates.length">
            Missing: {{ missingDates.join(", ") }}.
          </span>
        </div>
        <div v-if="captureStatus" class="mt-2 caption">{{ captureStatus }}</div>
        <v-alert v-if="captureError" type="error" dense text class="mt-3">
          {{ captureError }}
        </v-alert>
        <v-alert
          v-if="!loading && daysCaptured === 0"
          type="info"
          dense
          text
          class="mt-3"
        >
          No activity has been stored for these dates yet. Capture the missing
          days to rank the divisions.
        </v-alert>
      </v-card-text>
    </v-card>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    ></v-progress-linear>

    <v-card v-for="section in sections" :key="section.key" class="mb-4">
      <v-card-title>
        {{ section.title }}
        <span v-if="section.subtitle" class="subtitle-1 grey--text ml-3">
          {{ section.subtitle }}
        </span>
      </v-card-title>
      <v-card-text>
        <div v-for="group in section.groups" :key="group.role" class="mb-6">
          <div class="subtitle-1 mb-2">{{ group.label }}</div>
          <div style="overflow-x: auto">
            <v-data-table
              :headers="section.headers"
              :items="group.people"
              :items-per-page="-1"
              hide-default-footer
              dense
            >
              <template v-slot:item.score="{ item }">
                {{ formatScore(item.score) }}
              </template>
              <template v-slot:item.priorScore="{ item }">
                {{ formatScore(item.priorScore) }}
              </template>
              <template v-slot:item.change="{ item }">
                <span :class="changeClass(item.change)">{{
                  formatChange(item.change)
                }}</span>
              </template>
              <template v-slot:item.productiveHours="{ item }">
                {{ formatScore(item.productiveHours) }}
              </template>
              <template v-slot:item.priorProductiveHours="{ item }">
                {{ formatScore(item.priorProductiveHours) }}
              </template>
              <template v-slot:item.scorePerHour="{ item }">
                {{ formatScore(item.scorePerHour) }}
              </template>
              <template v-slot:no-data>No one in this role.</template>
            </v-data-table>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-title>Weights</v-card-title>
      <v-card-text>
        <div class="caption grey--text mb-4">
          A person's score is each raw count times that role's weight. The same
          weights apply in every division. Saving recalculates the ranks above.
        </div>
        <v-row>
          <v-col
            v-for="role in weightDrafts"
            :key="role.teamRole"
            cols="12"
            md="6"
          >
            <div class="subtitle-1 mb-2">{{ roleLabel(role.teamRole) }}</div>
            <v-text-field
              v-for="field in weightFields"
              :key="field.key"
              v-model.number="role[field.key]"
              :label="field.label"
              type="number"
              step="0.25"
              dense
              outlined
              hide-details
              class="mb-2"
            ></v-text-field>
            <v-btn
              color="primary"
              :loading="savingRole === role.teamRole"
              @click="saveWeights(role)"
            >
              Save {{ roleLabel(role.teamRole).toLowerCase() }} weights
            </v-btn>
          </v-col>
        </v-row>
        <v-alert v-if="weightError" type="error" dense text class="mt-3">
          {{ weightError }}
        </v-alert>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import format from "date-fns/format";
import subDays from "date-fns/subDays";

const COUNT_FIELDS = `
  casesVisited
  casesWorked
  casesCreated
  notesCreated
  notesModified
  notesDeleted
  fileReviews
  clientContacts
  docsGenerated
  docsAttached
  docsScanned
  docsDeleted
  tasksCreated
  tasksModified
  tasksCompleted
  negotiationsCreated
  negotiationsModified
  settlementsCreated
  settlementsModified
`;

const weightFields = [
  { key: "casesVisited", label: "Cases visited" },
  { key: "casesWorked", label: "Cases worked" },
  { key: "casesCreated", label: "Cases created" },
  { key: "notesCreated", label: "Notes created" },
  { key: "notesModified", label: "Notes modified" },
  { key: "notesDeleted", label: "Notes deleted" },
  { key: "fileReviews", label: "File reviews" },
  { key: "clientContacts", label: "Client contacts" },
  { key: "docsGenerated", label: "Documents generated" },
  { key: "docsAttached", label: "Documents attached" },
  { key: "docsScanned", label: "Documents scanned" },
  { key: "docsDeleted", label: "Documents deleted" },
  { key: "tasksCreated", label: "Tasks created" },
  { key: "tasksModified", label: "Tasks modified" },
  { key: "tasksCompleted", label: "Tasks completed" },
  { key: "negotiationsCreated", label: "Negotiations created" },
  { key: "negotiationsModified", label: "Negotiations modified" },
  { key: "settlementsCreated", label: "Settlements created" },
  { key: "settlementsModified", label: "Settlements modified" }
];

export default {
  data() {
    const end = subDays(new Date(), 1);
    const start = subDays(end, 6);
    return {
      startDate: format(start, "yyyy-MM-dd"),
      endDate: format(end, "yyyy-MM-dd"),
      divisionId: "all",
      firmView: localStorage.getItem("RyBase.staffActivity.firmView") === "1",
      capturing: false,
      captureStatus: "",
      captureError: null,
      savingRole: null,
      weightError: null,
      weightDrafts: [],
      weightFields
    };
  },
  apollo: {
    staffActivity: {
      query: gql`
        query($startDate: String!, $endDate: String!) {
          staffActivity(startDate: $startDate, endDate: $endDate) {
            startDate
            endDate
            daysCaptured
            computerDaysCaptured
            missingDates
            divisions {
              id
              name
              managerName
              groups {
                role
                label
                people {
                  userId
                  name
                  teams
                  rank
                  score
                  priorScore
                  change
                  productiveHours
                  priorProductiveHours
                  scorePerHour
                  ${COUNT_FIELDS}
                }
              }
            }
            weights {
              teamRole
              ${COUNT_FIELDS}
            }
          }
        }
      `,
      variables() {
        return { startDate: this.startDate, endDate: this.endDate };
      },
      result({ data }) {
        const weights =
          data && data.staffActivity && data.staffActivity.weights;
        if (weights) this.weightDrafts = weights.map(role => ({ ...role }));
      },
      fetchPolicy: "network-only"
    }
  },
  computed: {
    loading() {
      return this.$apollo.queries.staffActivity.loading;
    },
    daysCaptured() {
      return (this.staffActivity && this.staffActivity.daysCaptured) || 0;
    },
    computerDaysCaptured() {
      return (
        (this.staffActivity && this.staffActivity.computerDaysCaptured) || 0
      );
    },
    missingDates() {
      return (this.staffActivity && this.staffActivity.missingDates) || [];
    },
    divisionOptions() {
      const divisions =
        (this.staffActivity && this.staffActivity.divisions) || [];
      return [
        { text: "All divisions", value: "all" },
        ...divisions.map(division => ({
          text: division.name,
          value: division.id
        }))
      ];
    },
    visibleDivisions() {
      const divisions =
        (this.staffActivity && this.staffActivity.divisions) || [];
      if (this.divisionId === "all") return divisions;
      return divisions.filter(division => division.id === this.divisionId);
    },
    firmGroups() {
      const divisions =
        (this.staffActivity && this.staffActivity.divisions) || [];
      const people = { attorney: [], paralegal: [] };
      const seen = { attorney: {}, paralegal: {} };

      divisions.forEach(division => {
        (division.groups || []).forEach(group => {
          (group.people || []).forEach(person => {
            const existing =
              seen[group.role] && seen[group.role][person.userId];
            if (existing) {
              existing.division = this.joinUnique(
                existing.division,
                division.name
              );
              existing.teams = this.joinUnique(existing.teams, person.teams);
              return;
            }
            const copy = { ...person, division: division.name };
            seen[group.role][person.userId] = copy;
            people[group.role].push(copy);
          });
        });
      });

      return [
        {
          role: "attorney",
          label: "Attorneys",
          people: this.rankPeople(people.attorney)
        },
        {
          role: "paralegal",
          label: "Case managers",
          people: this.rankPeople(people.paralegal)
        }
      ];
    },
    sections() {
      if (this.firmView) {
        return [
          {
            key: "firm",
            title: "Firm",
            subtitle: null,
            headers: this.firmHeaders,
            groups: this.firmGroups
          }
        ];
      }
      return this.visibleDivisions.map(division => ({
        key: division.id,
        title: division.name,
        subtitle: division.managerName,
        headers: this.headers,
        groups: division.groups
      }));
    },
    firmHeaders() {
      const headers = this.headers.slice();
      headers.splice(2, 0, { text: "Division", value: "division" });
      return headers;
    },
    headers() {
      return [
        { text: "Rank", value: "rank", width: "70px" },
        { text: "Name", value: "name" },
        { text: "Teams", value: "teams" },
        { text: "Score", value: "score" },
        { text: "Prior", value: "priorScore" },
        { text: "Change", value: "change" },
        { text: "Hours", value: "productiveHours" },
        { text: "Prior hrs", value: "priorProductiveHours" },
        { text: "Score/hr", value: "scorePerHour" },
        { text: "Visited", value: "casesVisited" },
        { text: "Notes", value: "notesCreated" },
        { text: "File review", value: "fileReviews" },
        { text: "Client contact", value: "clientContacts" },
        { text: "Docs generated", value: "docsGenerated" },
        { text: "Docs attached", value: "docsAttached" },
        { text: "Docs scanned", value: "docsScanned" },
        { text: "Tasks done", value: "tasksCompleted" },
        { text: "Negotiations", value: "negotiationsCreated" },
        { text: "Settlements", value: "settlementsCreated" }
      ];
    }
  },
  watch: {
    firmView(value) {
      localStorage.setItem("RyBase.staffActivity.firmView", value ? "1" : "0");
    }
  },
  methods: {
    joinUnique(current, next) {
      const values = `${current || ""},${next || ""}`
        .split(",")
        .map(value => value.trim())
        .filter(Boolean);
      return [...new Set(values)].join(", ");
    },
    rankPeople(people) {
      return people
        .slice()
        .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name))
        .map((person, index) => ({ ...person, rank: index + 1 }));
    },
    formatScore(value) {
      if (value === null || value === undefined) return "—";
      return Number(value).toFixed(1);
    },
    formatChange(value) {
      if (value === null || value === undefined) return "—";
      const number = Number(value);
      const sign = number > 0 ? "+" : "";
      return sign + number.toFixed(1);
    },
    changeClass(value) {
      if (value === null || value === undefined || Number(value) === 0)
        return "";
      return Number(value) > 0 ? "green--text" : "red--text";
    },
    roleLabel(role) {
      return role === "attorney" ? "Attorneys" : "Case managers";
    },
    async captureMissing() {
      this.capturing = true;
      this.captureError = null;
      const dates = [...this.missingDates];
      for (const date of dates) {
        this.captureStatus = `Capturing ${date}...`;
        try {
          const result = await this.$apollo.mutate({
            mutation: gql`
              mutation($date: String!) {
                captureStaffActivity(date: $date) {
                  activityDate
                  rows
                  computerRows
                  computerError
                }
              }
            `,
            variables: { date }
          });
          const captured = result.data && result.data.captureStaffActivity;
          if (captured && captured.computerError) {
            this.captureError = captured.computerError;
            if (!captured.computerError.includes("not configured")) break;
          }
        } catch (error) {
          const graphqlError = error.graphQLErrors && error.graphQLErrors[0];
          this.captureError =
            (graphqlError && graphqlError.message) ||
            `Could not capture ${date}.`;
          break;
        }
      }
      this.captureStatus = "";
      this.capturing = false;
      await this.$apollo.queries.staffActivity.refetch();
    },
    async saveWeights(role) {
      this.savingRole = role.teamRole;
      this.weightError = null;
      const weights = { teamRole: role.teamRole };
      this.weightFields.forEach(field => {
        weights[field.key] = Number(role[field.key]) || 0;
      });
      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation($weights: StaffActivityWeightsInput!) {
              updateStaffActivityWeights(weights: $weights) {
                teamRole
              }
            }
          `,
          variables: { weights }
        });
        await this.$apollo.queries.staffActivity.refetch();
      } catch (error) {
        const graphqlError = error.graphQLErrors && error.graphQLErrors[0];
        this.weightError =
          (graphqlError && graphqlError.message) || "Could not save weights.";
      }
      this.savingRole = null;
    }
  }
};
</script>
